import * as React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChecklistIcon from '@mui/icons-material/Checklist';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import {
  buildNameIndex,
  confidentPick,
  scoreName,
  Suggestion,
  suggestNames,
} from 'components/ReportRequestV2/FuzzyFindDialog/nameRepair';
import useProductImages from 'hooks/useProductImages';

import styles from './RepairNamesDialog.module.css';
import RepairSummary, { RepairChoices } from './RepairSummary';
import SuggestionList from './SuggestionList';
import type { CatalogProduct } from '../ProductPicker';

export type { RepairChoices };

export interface RepairNamesDialogProps {
  open: boolean;
  /** Names that need a person to confirm what they are. */
  names: string[];
  catalog: CatalogProduct[];
  /** Decisions a person already made, e.g. in an earlier pass; kept as they are. */
  initialChoices?: RepairChoices;
  /** Name to open on, e.g. the one the user clicked. Defaults to the first unreviewed name. */
  startAt?: string;
  /** Names already in the set; flagged on their cards. */
  addedNames?: ReadonlySet<string>;
  onClose: () => void;
  /** Every decision made, confirmed names and skips; unreviewed names are left out. */
  onDone: (choices: RepairChoices) => void;
}

/** Results follow typing after a short pause, so the list doesn't re-rank on every keystroke. */
const SETTLE_MS = 150;

/**
 * Trails `value` while typing within one `key`, but follows a new key at once,
 * so moving to another name never shows (or confirms) the previous name's results.
 */
const useSettled = (value: string, key: string) => {
  const [settled, setSettled] = React.useState({ key, value });
  React.useEffect(() => {
    if (settled.key === key && settled.value === value) return undefined;
    if (settled.key !== key) {
      setSettled({ key, value });
      return undefined;
    }
    const timer = setTimeout(() => setSettled({ key, value }), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [key, value, settled]);
  return settled.key === key ? settled.value : value;
};

const isTyping = (target: EventTarget) =>
  (target as HTMLElement).tagName === 'INPUT';

const statusIcon = (choice: string | null | undefined) => {
  if (choice)
    return (
      <CheckCircleIcon
        fontSize="small"
        color="success"
        titleAccess="Confirmed"
      />
    );
  if (choice === null)
    return (
      <RemoveCircleOutlineIcon
        fontSize="small"
        color="disabled"
        titleAccess="Skipped"
      />
    );
  return (
    <RadioButtonUncheckedIcon
      fontSize="small"
      color="warning"
      titleAccess="Not reviewed"
    />
  );
};

/**
 * The human check on name repair: one name at a time, each with its closest
 * catalog products (photos, workspaces, reviews, matched words). A match only
 * counts once someone confirms it; the best one is highlighted, so a correct
 * suggestion is a single Enter. The search can be edited to fix a name the
 * catalog doesn't recognise, and every decision can be revisited before Done.
 */
const RepairNamesDialog: React.FC<RepairNamesDialogProps> = ({
  open,
  names,
  catalog,
  initialChoices,
  startAt,
  addedNames,
  onClose,
  onDone,
}) => {
  const index = React.useMemo(
    () => (open ? buildNameIndex(catalog) : []),
    [open, catalog],
  );
  const byName = React.useMemo(
    () => new Map(index.map((e) => [e.name, e])),
    [index],
  );
  // Each session starts from the caller's decisions, at the requested or first unreviewed name.
  const startChoices = (): RepairChoices =>
    Object.fromEntries(
      names
        .filter((n) => initialChoices && n in initialChoices)
        .map((n) => [n, initialChoices?.[n] ?? null]),
    );
  const startPosition = (start: RepairChoices) => {
    const requested = startAt ? names.indexOf(startAt) : -1;
    const firstOpen = names.findIndex((n) => !(n in start));
    return requested >= 0 ? requested : Math.max(firstOpen, 0);
  };
  const [choices, setChoices] = React.useState<RepairChoices>(startChoices);
  const [queries, setQueries] = React.useState<Record<string, string>>({});
  // `names.length` is the review of every decision.
  const [position, setPosition] = React.useState(() => startPosition(choices));
  const [highlight, setHighlight] = React.useState({ key: '', index: 0 });
  const inputRef = React.useRef<HTMLInputElement>(null);

  // A dialog kept mounted between uses starts afresh each time it reopens.
  const wasOpen = React.useRef(open);
  React.useEffect(() => {
    if (open && !wasOpen.current) {
      const start = startChoices();
      setChoices(start);
      setQueries({});
      setPosition(startPosition(start));
    }
    wasOpen.current = open;
    // Only reopening resets the session; prop changes while open must not discard decisions.
  }, [open]);

  const current = position < names.length ? names[position] : null;
  const query = current ? queries[current] ?? current : '';
  const deferredQuery = useSettled(query, current ?? '');
  const chosen = current ? choices[current] : undefined;

  const suggestions = React.useMemo((): Suggestion[] => {
    if (!current) return [];
    const found = suggestNames(deferredQuery, index);
    const entry = chosen ? byName.get(chosen) : undefined;
    // A confirmed match stays on screen even when an edited search no longer ranks it.
    return entry && !found.some((s) => s.entry === entry)
      ? [{ entry, score: scoreName(deferredQuery, entry) }, ...found]
      : found;
  }, [current, deferredQuery, index, byName, chosen]);

  const highlightKey = `${position}\u0000${deferredQuery}`;
  const highlighted =
    highlight.key === highlightKey
      ? highlight.index
      : Math.max(
          suggestions.findIndex((s) => s.entry.name === chosen),
          0,
        );
  const images = useProductImages(
    open ? suggestions.map((s) => s.entry.products[0].id) : [],
  );

  React.useEffect(() => {
    inputRef.current?.focus();
  }, [position]);

  const reviewed = names.filter((n) => n in choices).length;
  const unreviewed = names.length - reviewed;
  // Best match per name and search. Each scan covers the whole catalog, so a
  // keystroke or decision only rescans what changed.
  const bestCache = React.useRef({
    index,
    byKey: new Map<string, Suggestion | null>(),
  });
  // Confident suggestions for names nobody has looked at yet, for the explicit bulk confirm.
  const confident = React.useMemo(() => {
    if (bestCache.current.index !== index)
      bestCache.current = { index, byKey: new Map() };
    const { byKey } = bestCache.current;
    return names.flatMap((n) => {
      if (n in choices) return [];
      const q = queries[n] ?? n;
      const key = `${n}\u0000${q}`;
      if (!byKey.has(key))
        byKey.set(key, confidentPick(q, suggestNames(q, index, 2)));
      const best = byKey.get(key);
      return best ? [[n, best.entry.name] as const] : [];
    });
  }, [names, choices, queries, index]);

  const nextOpen = (from: number, next: RepairChoices) => {
    for (let step = 1; step <= names.length; step += 1) {
      const i = (from + step) % names.length;
      if (!(names[i] in next)) return i;
    }
    return names.length;
  };

  const decide = (value: string | null) => {
    if (!current) return;
    const next = { ...choices, [current]: value };
    setChoices(next);
    setPosition(nextOpen(position, next));
  };

  const confirmSuggested = () => {
    const next = { ...choices, ...Object.fromEntries(confident) };
    setChoices(next);
    // Land on the review, so the bulk-confirmed matches are still seen before Done.
    setPosition(names.length);
  };

  const moveHighlight = (delta: number) => {
    if (!suggestions.length) return;
    setHighlight({
      key: highlightKey,
      index: (highlighted + delta + suggestions.length) % suggestions.length,
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onDone(choices);
    } else if (!current) {
      // Nothing else applies on the review.
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      decide(null);
    } else if (e.key === 'Enter' && isTyping(e.target)) {
      e.preventDefault();
      // Until the results catch up with an edit, the highlighted match belongs to the
      // previous search; confirming it would add a product the person never saw.
      if (query === deferredQuery && suggestions[highlighted])
        decide(suggestions[highlighted].entry.name);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ className: styles.paper, onKeyDown }}
      aria-labelledby="repair-names-title"
    >
      <DialogTitle id="repair-names-title" className={styles.title}>
        <Box className={styles.titleRow}>
          <span>Repair product names</span>
          <Typography variant="body2" color="text.secondary" aria-live="polite">
            {reviewed} of {names.length} reviewed
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={names.length ? (reviewed / names.length) * 100 : 0}
          className={styles.progress}
          aria-label="Review progress"
        />
      </DialogTitle>
      <DialogContent className={styles.body} dividers>
        <List dense aria-label="Names" className={styles.nav}>
          {names.map((name, i) => (
            <ListItemButton
              key={name}
              selected={i === position}
              onClick={() => setPosition(i)}
              className={styles.navItem}
            >
              <ListItemIcon className={styles.navIcon}>
                {statusIcon(choices[name])}
              </ListItemIcon>
              <ListItemText
                primary={name}
                secondary={
                  choices[name] ??
                  (choices[name] === null ? 'Skipped' : 'Not reviewed')
                }
                primaryTypographyProps={{ noWrap: true, title: name }}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </ListItemButton>
          ))}
          <ListItemButton
            selected={position === names.length}
            onClick={() => setPosition(names.length)}
          >
            <ListItemIcon className={styles.navIcon}>
              <ChecklistIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Review all" />
          </ListItemButton>
        </List>

        <Box className={styles.main}>
          {current ? (
            <>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Name {position + 1} of {names.length} · as pasted
                </Typography>
                <Typography variant="h6" className={styles.pasted}>
                  {current}
                </Typography>
              </Box>
              <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                label="Search the catalog"
                value={query}
                onChange={(e) =>
                  setQueries((q) => ({ ...q, [current]: e.target.value }))
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment:
                    query !== current ? (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={() =>
                            setQueries((q) => ({ ...q, [current]: current }))
                          }
                        >
                          Reset
                        </Button>
                      </InputAdornment>
                    ) : undefined,
                }}
                helperText="Fix a typo or drop extra words; matches update as you type."
              />
              <SuggestionList
                pasted={current}
                suggestions={suggestions}
                query={deferredQuery}
                highlighted={highlighted}
                chosen={chosen}
                images={images}
                addedNames={addedNames}
                onHighlight={(i) =>
                  setHighlight({ key: highlightKey, index: i })
                }
                onChoose={(name) => decide(name)}
              />
            </>
          ) : (
            <RepairSummary
              names={names}
              choices={choices}
              onRevisit={setPosition}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions className={styles.actions}>
        <Typography
          variant="caption"
          color="text.secondary"
          className={styles.keys}
        >
          <kbd>↑</kbd>
          <kbd>↓</kbd> choose · <kbd>Enter</kbd> confirm · <kbd>⇧ Enter</kbd>{' '}
          skip · <kbd>⌘/Ctrl Enter</kbd> done
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          disabled={position === 0}
          onClick={() => setPosition((p) => Math.max(p - 1, 0))}
        >
          Back
        </Button>
        {current && (
          <Button
            onClick={() => decide(null)}
            color={chosen === null ? 'primary' : 'inherit'}
          >
            {chosen === null ? 'Skipped' : 'Skip'}
          </Button>
        )}
        {confident.length > 0 && (
          <Button onClick={confirmSuggested}>
            Confirm {confident.length} suggested
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onDone(choices)}>
          {unreviewed > 0 ? `Done, ${unreviewed} left unmatched` : 'Done'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RepairNamesDialog;
